import React, { useContext, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Dropdown, Button, Form } from 'semantic-ui-react'

import constants from 'config/constants'
import useModal from 'hooks/useModal'
import SurveyAccessFormModalContainer from 'components/SurveyAccessFormModal/container'
import ModalClickBoundary from 'shared/components/ModalClickBoundary'
import Popup from 'shared/components/Popup'
import { useDynamicConstants } from 'config/dynamicConstants'
import useFeatureToggle from 'hooks/useFeatureToggle'
import usePermission from 'hooks/usePermission'
import { surveyHasFlippableQuestion } from 'libs/questions'
import useSurveyQuestions from 'hooks/useSurveyQuestions'
import { surveySettingContext } from 'pages/SurveySettings/context'

const CHOICE_ORDER_KB_URL =
  'https://help.simplesat.io/en/articles/2776705-survey-questions#choice-order'

export const SurveyConfigOptions = ({
  survey,
  handleUpdateSurveyStatus,
  createOnRemoveDeliveryMethod,
  updateSurveyLoading,
  duplicateSurvey,
  duplicateSurveyLoading,
  flipChoicesOrder,
  flipChoicesOrderLoading,
  isReverseOrder,
}) => {
  const { enableRatingChoiceOrder } = useFeatureToggle()
  const { hasPermission } = usePermission()

  const hasManageSurveysPermission = hasPermission(constants.permissions.manageSurveys)
  const hasManageUserAccessToSurveysPermission = hasPermission(
    constants.permissions.manageUserAccessToSurveys
  )

  if (hasManageSurveysPermission || hasManageUserAccessToSurveysPermission) {
    return (
      <Dropdown
        text=""
        icon="far fa-ellipsis-vertical"
        floating
        pointing
        button
        openOnFocus={false}
        className="bg-white text-black basic top right options"
      >
        <Dropdown.Menu>
          {hasManageSurveysPermission && (
            <ChangeDeliveryTypeMenuItem
              surveyType={survey.type}
              createOnRemoveDeliveryMethod={createOnRemoveDeliveryMethod}
              loading={updateSurveyLoading}
            />
          )}
          {hasManageUserAccessToSurveysPermission && (
            <SurveyAccessFormModalContainer surveyToken={survey.token} />
          )}

          {hasManageSurveysPermission && (
            <React.Fragment>
              {enableRatingChoiceOrder && (
                <FlipChoicesOrderMenuItem
                  surveyToken={survey.token}
                  flipChoicesOrder={flipChoicesOrder}
                  loading={flipChoicesOrderLoading}
                  isReverseOrder={isReverseOrder}
                />
              )}
              <DuplicateSurvey duplicateSurvey={duplicateSurvey} loading={duplicateSurveyLoading} />
              <ArchiveMenuItem
                surveyStatus={survey.status}
                handleUpdateSurveyStatus={handleUpdateSurveyStatus}
              />
            </React.Fragment>
          )}
        </Dropdown.Menu>
      </Dropdown>
    )
  } else {
    return null
  }
}

SurveyConfigOptions.propTypes = {
  survey: PropTypes.object,
  handleUpdateSurveyStatus: PropTypes.func,
}

function ArchiveMenuItem({ surveyStatus, handleUpdateSurveyStatus }) {
  if (surveyStatus === 'active') {
    return (
      <Dropdown.Item
        text="Archive"
        icon="far fa-archive"
        onClick={() => handleUpdateSurveyStatus('archived')}
      />
    )
  }

  if (surveyStatus === 'archived') {
    return (
      <Dropdown.Item
        text="Unarchive"
        icon="far fa-archive"
        onClick={() => handleUpdateSurveyStatus('active')}
      />
    )
  }
  return null
}

function ChangeDeliveryTypeMenuItem({ surveyType, createOnRemoveDeliveryMethod, loading }) {
  const dynamicConstants = useDynamicConstants()
  const hasDeliveryMethod =
    dynamicConstants.embedSurveyTypes.includes(surveyType) ||
    'web_embed' ||
    constants.emailSurveyTypes.includes(surveyType)

  const { Modal, open, close } = useModal()
  if (hasDeliveryMethod) {
    return (
      <React.Fragment>
        <ModalClickBoundary>
          <Modal size="tiny">
            <Modal.Header>Remove delivery method</Modal.Header>
            <Modal.Content>
              Removing the current delivery method will prevent future emails from sending and
              ticket integrations from running. However, your customers will still be able to
              respond to the survey, and any previous settings will be saved.
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={close}>Cancel</Button>
              <Button
                negative
                onClick={createOnRemoveDeliveryMethod(close)}
                loading={loading}
                disabled={loading}
              >
                Remove current delivery method
              </Button>
            </Modal.Actions>
          </Modal>
        </ModalClickBoundary>
        <Dropdown.Item text="Change delivery method" icon="far fa-parachute-box" onClick={open} />
      </React.Fragment>
    )
  }

  return null
}

function FlipChoicesOrderMenuItem({ surveyToken, flipChoicesOrder, loading, isReverseOrder }) {
  const { Modal, open, close } = useModal()
  const [pendingValue, setPendingValue] = useState(Boolean(isReverseOrder))
  const context = useContext(surveySettingContext)
  const builderFlippable = context && context.hasFlippableBuilderQuestion
  const shouldSkipFetch = builderFlippable !== null && builderFlippable !== undefined
  const fetchedQuestions = useSurveyQuestions(shouldSkipFetch ? null : surveyToken)
  const isFlippable = shouldSkipFetch
    ? builderFlippable
    : surveyHasFlippableQuestion(fetchedQuestions)

  if (!isFlippable) {
    return (
      <Popup basic tooltip="No questions in this survey can be flipped." position="left center">
        <Dropdown.Item
          text="Flip choice order"
          icon={<i className="icon far fa-arrow-up-arrow-down" />}
          disabled
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          style={{ pointerEvents: 'auto' }}
        />
      </Popup>
    )
  }

  function handleOpen() {
    setPendingValue(Boolean(isReverseOrder))
    open()
  }

  async function handleSave() {
    try {
      await flipChoicesOrder(pendingValue)
      close()
    } catch {
      // keep modal open so the user sees the error surface + can retry
    }
  }

  return (
    <React.Fragment>
      <ModalClickBoundary>
        <Modal size="tiny">
          <Modal.Header>Flip choice order</Modal.Header>
          <Modal.Content>
            <p>
              Choose how rating icons display to respondents. The order applies everywhere this
              survey appears; builder, preview, link, and email and on every device.{' '}
              <a href={CHOICE_ORDER_KB_URL} target="_blank" rel="noopener noreferrer">
                <i className="far fa-book icon" />
                Learn more
              </a>
            </p>
            <Form>
              <Form.Group grouped>
                <label>Display order</label>
                <Form.Checkbox
                  checked={!pendingValue}
                  onChange={() => setPendingValue(false)}
                  radio
                  value="false"
                  label="Positive to negative"
                  name="flipChoicesOrder"
                />
                <Form.Checkbox
                  checked={pendingValue}
                  onChange={() => setPendingValue(true)}
                  radio
                  value="true"
                  label="Negative to positive"
                  name="flipChoicesOrder"
                />
              </Form.Group>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={close}>Cancel</Button>
            <Button primary onClick={handleSave} loading={loading} disabled={loading}>
              Save
            </Button>
          </Modal.Actions>
        </Modal>
      </ModalClickBoundary>
      <Dropdown.Item
        text="Flip choice order"
        icon={<i className="icon far fa-arrow-up-arrow-down" />}
        onClick={handleOpen}
      />
    </React.Fragment>
  )
}

function DuplicateSurvey({ loading, duplicateSurvey }) {
  const { Modal, open, close } = useModal()
  const [surveyName, setSurveyName] = useState('')
  const hiddenButtonRef = useRef()

  function handleOnSubmit() {
    duplicateSurvey(surveyName)
  }
  return (
    <React.Fragment>
      <ModalClickBoundary>
        <Modal size="tiny">
          <Modal.Header>Duplicate survey</Modal.Header>
          <Modal.Content>
            <Form onSubmit={handleOnSubmit}>
              <Form.Field>
                <label>Survey name</label>
                <input
                  value={surveyName}
                  onChange={(e) => setSurveyName(e.target.value)}
                  required
                  autoFocus
                />
              </Form.Field>
              <input type="submit" hidden ref={hiddenButtonRef} />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={close}>Cancel</Button>
            <Button
              primary
              onClick={() => hiddenButtonRef.current.click()}
              loading={loading}
              disabled={loading}
            >
              Create new survey
            </Button>
          </Modal.Actions>
        </Modal>
      </ModalClickBoundary>
      <Dropdown.Item text="Duplicate survey" icon="far fa-clone" onClick={open} />
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SurveyConfigOptions/index.js